import { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Input from '../common/Input';
import Button from '../common/Button';
import MultiSelect from '../common/MultiSelect';
import { validateForm } from '../../utils/validators';
import {
  uploadVariantLabel, deleteVariantLabel, fetchVariantLabelBlobUrl,
  uploadProductImage, deleteProductImage, fetchProductImageBlobUrl,
} from '../../services/products';
import { ENDPOINTS } from '../../utils/constants';
import './ProductForm.css';

const VALIDATION_RULES = {
  name: { required: true, maxLength: 255 },
};

const INITIAL_FORM = {
  name: '',
  category_ids: [],
  description: '',
  ingredients: '',
  is_active: true,
  grain_variety_ids: [],
  grower_ids: [],
};

const makeVariantRow = (data = {}) => ({
  _key: data.id ? String(data.id) : crypto.randomUUID(),
  id: data.id || null,
  variant_name: data.variant_name || '',
  sku: data.sku || '',
  price_dollars: data.price_dollars || '',
  unit: data.unit || '',
  upc_code: data.upc_code || '',
  net_weight: data.net_weight || '',
  net_weight_lbs: data.net_weight_lbs ?? '',
  // label data comes from the API via label_url / label_image_name
  label_url: data.label_url || '',
  label_image_name: data.label_image_name || '',
  is_active: data.is_active !== undefined ? data.is_active : true,
});

/**
 * Django-admin-style product form with fieldsets and inline variant editor
 */
const ProductForm = ({ product, choices, onSave, onCancel, isSaving }) => {
  const [form, setForm] = useState(INITIAL_FORM);
  const [variants, setVariants] = useState([]);
  const [errors, setErrors] = useState({});
  const [variantErrors, setVariantErrors] = useState({});

  // Product-level image upload state
  const [imageState, setImageState] = useState({ uploading: false, error: '' });
  // Product image blob URL (for thumbnail preview)
  const [imageBlobUrl, setImageBlobUrl] = useState('');
  const productImageInputRef = useRef(null);

  // Per-variant label upload state: { [_key]: { uploading: bool, error: string } }
  const [labelState, setLabelState] = useState({});
  // Hidden file inputs: { [_key]: HTMLInputElement ref }
  const fileInputRefs = useRef({});

  // Populate form when editing
  useEffect(() => {
    if (product) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setForm({
        name: product.name || '',
        category_ids: product.categories_detail
          ? product.categories_detail.map((c) => c.id)
          : [],
        description: product.description || '',
        ingredients: product.ingredients || '',
        is_active: product.is_active !== undefined ? product.is_active : true,
        grain_variety_ids: product.grain_varieties_detail
          ? product.grain_varieties_detail.map((g) => g.id)
          : [],
        grower_ids: product.growers_detail
          ? product.growers_detail.map((g) => g.id)
          : [],
      });
      setVariants(
        (product.variants || []).map((v) => makeVariantRow(v))
      );
    }
  }, [product]);

  // Load product image blob when product has one
  useEffect(() => {
    if (!product?.id || !product?.image_url) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setImageBlobUrl('');
      return;
    }
    let objectUrl = '';
    fetchProductImageBlobUrl(product.id)
      .then((url) => {
        objectUrl = url;
        setImageBlobUrl(url);
      })
      .catch(() => {
        setImageBlobUrl('');
      });
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [product?.id, product?.image_url]);

  // Revoke any blob URL that was set after upload, if the component unmounts
  // before the user navigates away (e.g. closes the modal right after uploading).
  useEffect(() => {
    return () => {
      if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
    };
  }, [imageBlobUrl]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  const handleArrayChange = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => {
        const next = { ...prev };
        delete next[name];
        return next;
      });
    }
  };

  // ── Variant handlers ──

  const handleVariantChange = (key, field, value) => {
    setVariants((prev) =>
      prev.map((v) => (v._key === key ? { ...v, [field]: value } : v))
    );
    if (variantErrors[key]) {
      setVariantErrors((prev) => {
        const next = { ...prev };
        if (next[key]) {
          delete next[key][field];
          if (Object.keys(next[key]).length === 0) delete next[key];
        }
        return next;
      });
    }
  };

  const addVariant = () => {
    setVariants((prev) => [...prev, makeVariantRow()]);
  };

  const removeVariant = (key) => {
    setVariants((prev) => prev.filter((v) => v._key !== key));
    setVariantErrors((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    setLabelState((prev) => {
      const next = { ...prev };
      delete next[key];
      return next;
    });
    delete fileInputRefs.current[key];
  };

  // ── Label upload/delete handlers ──

  const setLabelUploading = (key, uploading, error = '') => {
    setLabelState((prev) => ({
      ...prev,
      [key]: { uploading, error },
    }));
  };

  const handleLabelFileChange = async (variantKey, variantId, e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset input so the same file can be re-selected after a failure
    e.target.value = '';

    if (!variantId) {
      setLabelUploading(variantKey, false, 'Save the variant first before uploading a label.');
      return;
    }

    setLabelUploading(variantKey, true);
    try {
      const updated = await uploadVariantLabel(variantId, file);
      // Patch the variant row with the new label_url + name from the API response
      setVariants((prev) =>
        prev.map((v) =>
          v._key === variantKey
            ? { ...v, label_url: updated.label_url || '', label_image_name: updated.label_image_name || '' }
            : v
        )
      );
      setLabelUploading(variantKey, false);
    } catch (err) {
      setLabelUploading(variantKey, false, err?.message || 'Upload failed.');
    }
  };

  const handleLabelDelete = async (variantKey, variantId) => {
    if (!variantId) return;
    setLabelUploading(variantKey, true);
    try {
      await deleteVariantLabel(variantId);
      setVariants((prev) =>
        prev.map((v) =>
          v._key === variantKey
            ? { ...v, label_url: '', label_image_name: '' }
            : v
        )
      );
      setLabelUploading(variantKey, false);
    } catch (err) {
      setLabelUploading(variantKey, false, err?.message || 'Delete failed.');
    }
  };

  const handleLabelPrint = (variantId) => {
    if (!variantId) return;
    // Use the same /print-label route used by the order summary table.
    // That page handles auth via axios + renders into a full-page iframe.
    window.open(
      `/print-label?labelHtml=/api${ENDPOINTS.PRODUCTS.VARIANT_LABEL_HTML(variantId)}`,
      '_blank',
      'width=480,height=720',
    );
  };

  const handleLabelView = async (variantKey, variantId) => {
    if (!variantId) return;
    setLabelUploading(variantKey, true);
    try {
      const blobUrl = await fetchVariantLabelBlobUrl(variantId);
      setLabelUploading(variantKey, false);
      const win = window.open(blobUrl, '_blank');
      // Revoke after 2 minutes to give the new tab plenty of time to load the
      // image, even on slow connections or with large label files.
      if (win) {
        setTimeout(() => URL.revokeObjectURL(blobUrl), 120_000);
      } else {
        URL.revokeObjectURL(blobUrl);
      }
    } catch (err) {
      setLabelUploading(variantKey, false, err?.message || 'Could not load label.');
    }
  };

  // ── Product image upload/delete handlers ──

  const handleImageFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    e.target.value = '';

    if (!product?.id) {
      setImageState({ uploading: false, error: 'Save the product first before uploading an image.' });
      return;
    }

    setImageState({ uploading: true, error: '' });
    try {
      const updated = await uploadProductImage(product.id, file);
      // Clear the stale preview first so the <img> is removed from the DOM
      // before we revoke its object URL (avoids broken-image flash in some browsers).
      const oldUrl = imageBlobUrl;
      setImageBlobUrl('');
      if (oldUrl) URL.revokeObjectURL(oldUrl);
      if (updated.image_url) {
        const newBlobUrl = await fetchProductImageBlobUrl(product.id);
        setImageBlobUrl(newBlobUrl);
      }
      setImageState({ uploading: false, error: '' });
    } catch (err) {
      setImageState({ uploading: false, error: err?.message || 'Upload failed.' });
    }
  };

  const handleImageDelete = async () => {
    if (!product?.id) return;
    setImageState({ uploading: true, error: '' });
    try {
      await deleteProductImage(product.id);
      if (imageBlobUrl) URL.revokeObjectURL(imageBlobUrl);
      setImageBlobUrl('');
      setImageState({ uploading: false, error: '' });
    } catch (err) {
      setImageState({ uploading: false, error: err?.message || 'Delete failed.' });
    }
  };

  // ── Validation & submit ──

  const validateVariants = () => {
    const errs = {};
    let valid = true;

    variants.forEach((v) => {
      const rowErrors = {};
      if (!v.variant_name.trim()) {
        rowErrors.variant_name = 'Required';
        valid = false;
      }
      if (!v.price_dollars && v.price_dollars !== 0) {
        rowErrors.price_dollars = 'Required';
        valid = false;
      } else if (isNaN(parseFloat(v.price_dollars)) || parseFloat(v.price_dollars) < 0) {
        rowErrors.price_dollars = 'Must be a valid positive number';
        valid = false;
      }
      if (!v.unit) {
        rowErrors.unit = 'Required';
        valid = false;
      }
      if (Object.keys(rowErrors).length > 0) {
        errs[v._key] = rowErrors;
      }
    });

    setVariantErrors(errs);
    return valid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const { isValid: formValid, errors: validationErrors } = validateForm(form, VALIDATION_RULES);
    const variantsValid = validateVariants();

    if (!formValid) {
      const flat = {};
      Object.entries(validationErrors).forEach(([k, v]) => {
        flat[k] = Array.isArray(v) ? v[0] : v;
      });
      setErrors(flat);
    }

    if (!formValid || !variantsValid) return;

    // Shape data for API — images are managed via separate upload/delete endpoints
    const payload = {
      name: form.name.trim(),
      category_ids: form.category_ids,
      description: form.description.trim(),
      ingredients: form.ingredients.trim(),
      is_active: form.is_active,
      grain_variety_ids: form.grain_variety_ids,
      grower_ids: form.grower_ids,
      variants_input: variants.map((v) => ({
        ...(v.id ? { id: v.id } : {}),
        variant_name: v.variant_name.trim(),
        sku: v.sku.trim() || '',
        price_cents: Math.round(parseFloat(v.price_dollars) * 100),
        unit: v.unit,
        upc_code: v.upc_code.trim(),
        net_weight: v.net_weight.trim(),
        net_weight_lbs: v.net_weight_lbs === '' ? null : v.net_weight_lbs,
        is_active: v.is_active,
      })),
    };

    onSave(payload);
  };

  // Merge choices with existing product data (handles deactivated items)
  const grainOptions = (() => {
    const choiceGrains = choices?.grain_varieties || [];
    const productGrains = product?.grain_varieties_detail || [];
    const map = new Map();
    choiceGrains.forEach((g) => map.set(g.id, g));
    productGrains.forEach((g) => {
      if (!map.has(g.id)) map.set(g.id, g);
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const growerOptions = (() => {
    const choiceGrowers = choices?.growers || [];
    const productGrowers = product?.growers_detail || [];
    const map = new Map();
    choiceGrowers.forEach((g) => map.set(g.id, { id: g.id, name: g.farm_name }));
    productGrowers.forEach((g) => {
      if (!map.has(g.id)) map.set(g.id, { id: g.id, name: g.farm_name });
    });
    return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
  })();

  const unitChoices = choices?.unit_choices || [];
  const categoryOptions = (choices?.categories || []).map((c) => ({ id: c.id, name: c.label }));

  return (
    <form className="product-form" onSubmit={handleSubmit} noValidate>
      {/* ── Fieldset 1: Product Information ──────────────── */}
      <fieldset className="product-form__fieldset">
        <legend className="product-form__legend">Product Information</legend>
        <div className="product-form__row">
          <Input
            label="Product Name"
            name="name"
            value={form.name}
            onChange={handleChange}
            error={errors.name}
            required
          />

          {/* ── Product image upload ── */}
          <div className="product-form__image-field">
            <span className="product-form__image-label">Product Image</span>

            {/* Hidden file input */}
            <input
              ref={productImageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageFileChange}
            />

            <div className="product-form__image-controls">
              {imageState.uploading ? (
                <span className="product-form__image-status">Loading…</span>
              ) : imageBlobUrl ? (
                <div className="product-form__image-preview-wrap">
                  <img
                    src={imageBlobUrl}
                    alt="Product"
                    className="product-form__image-preview"
                  />
                  <div className="product-form__image-actions">
                    <button
                      type="button"
                      className="product-form__image-btn product-form__image-btn--replace"
                      onClick={() => productImageInputRef.current?.click()}
                    >
                      Replace
                    </button>
                    <button
                      type="button"
                      className="product-form__image-btn product-form__image-btn--delete"
                      onClick={handleImageDelete}
                    >
                      ✕ Remove
                    </button>
                  </div>
                </div>
              ) : (
                <button
                  type="button"
                  className="product-form__image-btn product-form__image-btn--upload"
                  title={product?.id ? 'Upload product image' : 'Save the product first, then upload an image'}
                  disabled={!product?.id}
                  onClick={() => productImageInputRef.current?.click()}
                >
                  + Upload Image
                </button>
              )}
            </div>

            {imageState.error && (
              <span className="product-form__image-error">{imageState.error}</span>
            )}
            {!product?.id && (
              <span className="product-form__image-hint">Save first to enable upload</span>
            )}
          </div>
        </div>
        <div className="product-form__row product-form__row--full">
          <MultiSelect
            label="Categories"
            name="category_ids"
            options={categoryOptions}
            value={form.category_ids}
            onChange={handleArrayChange}
            placeholder="Select categories..."
          />
        </div>
        <div className="product-form__row product-form__row--full">
          <div className="input-wrapper">
            <label htmlFor="product-description" className="input-label">Description</label>
            <textarea
              id="product-description"
              name="description"
              value={form.description}
              onChange={handleChange}
              className="input product-form__textarea"
              rows={3}
              placeholder="Product description, usage notes..."
            />
          </div>
        </div>
        <div className="product-form__row product-form__row--full">
          <div className="input-wrapper">
            <label htmlFor="product-ingredients" className="input-label">
              Ingredients
              <span className="product-form__field-hint"> — printed on label</span>
            </label>
            <textarea
              id="product-ingredients"
              name="ingredients"
              value={form.ingredients}
              onChange={handleChange}
              className="input product-form__textarea"
              rows={2}
              placeholder="e.g. Organic hard red winter wheat"
            />
          </div>
        </div>
      </fieldset>

      {/* ── Fieldset 2: Grain Varieties ──────────────────── */}
      <fieldset className="product-form__fieldset">
        <legend className="product-form__legend">Grain Varieties</legend>
        <MultiSelect
          label="Grain varieties used in this product"
          name="grain_variety_ids"
          options={grainOptions}
          value={form.grain_variety_ids}
          onChange={handleArrayChange}
          placeholder="Select grain varieties..."
        />
      </fieldset>

      {/* ── Fieldset 3: Growers ──────────────────────────── */}
      <fieldset className="product-form__fieldset">
        <legend className="product-form__legend">Growers</legend>
        <MultiSelect
          label="Growers who supply grain for this product"
          name="grower_ids"
          options={growerOptions}
          value={form.grower_ids}
          onChange={handleArrayChange}
          placeholder="Select growers..."
        />
      </fieldset>

      {/* ── Fieldset 4: Variants ─────────────────────────── */}
      <fieldset className="product-form__fieldset">
        <legend className="product-form__legend">
          Variants
          <span className="product-form__variant-count">
            {variants.length} {variants.length === 1 ? 'variant' : 'variants'}
          </span>
        </legend>

        {variants.length > 0 && (
          <div className="product-form__variants">
            {/* Header row (desktop only) */}
            <div className="product-form__variant-header">
              <span>Name *</span>
              <span>SKU</span>
              <span>Price ($) *</span>
              <span>Unit *</span>
              <span>UPC Code</span>
              <span>Net Weight</span>
              <span>Net Wt (lbs)</span>
              <span>Label</span>
              <span>Print</span>
              <span>Active</span>
              <span></span>
            </div>

            {variants.map((v) => {
              const ls = labelState[v._key] || {};
              return (
                <div key={v._key} className="product-form__variant-row">
                  <div className="product-form__variant-field">
                    <label className="product-form__variant-mobile-label">Name *</label>
                    <input
                      type="text"
                      className={`input product-form__variant-input ${variantErrors[v._key]?.variant_name ? 'input--error' : ''}`}
                      value={v.variant_name}
                      onChange={(e) => handleVariantChange(v._key, 'variant_name', e.target.value)}
                      placeholder="e.g., Retail Bag"
                    />
                    {variantErrors[v._key]?.variant_name && (
                      <span className="product-form__variant-error">{variantErrors[v._key].variant_name}</span>
                    )}
                  </div>
                  <div className="product-form__variant-field">
                    <label className="product-form__variant-mobile-label">SKU</label>
                    <input
                      type="text"
                      className="input product-form__variant-input"
                      value={v.sku}
                      onChange={(e) => handleVariantChange(v._key, 'sku', e.target.value)}
                      placeholder="e.g., DRM-001"
                    />
                  </div>
                  <div className="product-form__variant-field">
                    <label className="product-form__variant-mobile-label">Price ($) *</label>
                    <input
                      type="number"
                      className={`input product-form__variant-input ${variantErrors[v._key]?.price_dollars ? 'input--error' : ''}`}
                      value={v.price_dollars}
                      onChange={(e) => handleVariantChange(v._key, 'price_dollars', e.target.value)}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                    />
                    {variantErrors[v._key]?.price_dollars && (
                      <span className="product-form__variant-error">{variantErrors[v._key].price_dollars}</span>
                    )}
                  </div>
                  <div className="product-form__variant-field">
                    <label className="product-form__variant-mobile-label">Unit *</label>
                    <select
                      className={`input product-form__variant-input product-form__variant-select ${variantErrors[v._key]?.unit ? 'input--error' : ''}`}
                      value={v.unit}
                      onChange={(e) => handleVariantChange(v._key, 'unit', e.target.value)}
                    >
                      <option value="">Select...</option>
                      {unitChoices.map((u) => (
                        <option key={u.value} value={u.value}>{u.label}</option>
                      ))}
                    </select>
                    {variantErrors[v._key]?.unit && (
                      <span className="product-form__variant-error">{variantErrors[v._key].unit}</span>
                    )}
                  </div>

                  <div className="product-form__variant-field">
                    <label className="product-form__variant-mobile-label">UPC Code</label>
                    <input
                      type="text"
                      className="input product-form__variant-input"
                      value={v.upc_code}
                      onChange={(e) => handleVariantChange(v._key, 'upc_code', e.target.value)}
                      placeholder="e.g. 709081353349"
                    />
                  </div>
                  <div className="product-form__variant-field">
                    <label className="product-form__variant-mobile-label">Net Weight</label>
                    <input
                      type="text"
                      className="input product-form__variant-input"
                      value={v.net_weight}
                      onChange={(e) => handleVariantChange(v._key, 'net_weight', e.target.value)}
                      placeholder="e.g. 10 lbs (4.5 kg)"
                    />
                  </div>
                  <div className="product-form__variant-field">
                    <label className="product-form__variant-mobile-label">Net Weight (lbs)</label>
                    <input
                      type="number"
                      step="0.001"
                      min="0"
                      className="input product-form__variant-input"
                      value={v.net_weight_lbs}
                      onChange={(e) => handleVariantChange(v._key, 'net_weight_lbs', e.target.value)}
                      placeholder="e.g. 10"
                    />
                  </div>

                  {/* ── Label cell ── */}
                  <div className="product-form__variant-field product-form__variant-field--label">
                    <label className="product-form__variant-mobile-label">Label</label>

                    {/* Hidden file input — triggered by the Upload / Replace button */}
                    <input
                      ref={(el) => { fileInputRefs.current[v._key] = el; }}
                      type="file"
                      accept="image/*"
                      style={{ display: 'none' }}
                      onChange={(e) => handleLabelFileChange(v._key, v.id, e)}
                    />

                    <div className="product-form__variant-label-actions">
                      {ls.uploading ? (
                        <span className="product-form__variant-label-status">Loading…</span>
                      ) : v.label_url ? (
                        <>
                          <button
                            type="button"
                            className="product-form__variant-label-link"
                            title={v.label_image_name || 'View label'}
                            onClick={() => handleLabelView(v._key, v.id)}
                          >
                            🏷 View
                          </button>
                          <button
                            type="button"
                            className="product-form__variant-label-btn product-form__variant-label-btn--replace"
                            title="Replace label image"
                            disabled={!v.id}
                            onClick={() => fileInputRefs.current[v._key]?.click()}
                          >
                            Replace
                          </button>
                          <button
                            type="button"
                            className="product-form__variant-label-btn product-form__variant-label-btn--delete"
                            title="Remove label image"
                            disabled={!v.id}
                            onClick={() => handleLabelDelete(v._key, v.id)}
                          >
                            ✕
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="product-form__variant-label-btn product-form__variant-label-btn--upload"
                          title={v.id ? 'Upload label image' : 'Save the product first, then upload a label'}
                          disabled={!v.id}
                          onClick={() => fileInputRefs.current[v._key]?.click()}
                        >
                          + Upload
                        </button>
                      )}
                    </div>

                    {ls.error && (
                      <span className="product-form__variant-label-error">{ls.error}</span>
                    )}
                    {!v.id && (
                      <span className="product-form__variant-label-hint">
                        Save first to enable upload
                      </span>
                    )}
                  </div>

                  {/* ── Print cell ── */}
                  <div className="product-form__variant-field product-form__variant-field--print">
                    <label className="product-form__variant-mobile-label">Print</label>
                    <button
                      type="button"
                      className="product-form__variant-print-btn"
                      title={v.id ? 'Print label' : 'Save the product first'}
                      disabled={!v.id}
                      onClick={() => handleLabelPrint(v.id)}
                      aria-label="Print label"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
                        <path d="M4 5V2h8v3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                        <rect x="1" y="5" width="14" height="7" rx="1.5" stroke="currentColor" strokeWidth="1.4"/>
                        <path d="M4 9h8M4 11.5h8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round"/>
                        <rect x="4" y="9" width="8" height="5" rx="0.5" stroke="currentColor" strokeWidth="1.4"/>
                      </svg>
                    </button>
                  </div>

                  <div className="product-form__variant-field product-form__variant-field--checkbox">
                    <label className="product-form__variant-mobile-label">Active</label>
                    <input
                      type="checkbox"
                      className="product-form__variant-checkbox"
                      checked={v.is_active}
                      onChange={(e) => handleVariantChange(v._key, 'is_active', e.target.checked)}
                    />
                  </div>
                  <div className="product-form__variant-field product-form__variant-field--action">
                    <button
                      type="button"
                      className="product-form__variant-remove"
                      onClick={() => removeVariant(v._key)}
                      title="Remove variant"
                      aria-label="Remove variant"
                    >
                      <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
                        <path d="M4 4l8 8M12 4l-8 8" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round"/>
                      </svg>
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        <button
          type="button"
          className="product-form__add-variant"
          onClick={addVariant}
        >
          + Add Variant
        </button>
      </fieldset>

      {/* ── Actions ──────────────────────────────────────── */}
      <div className="product-form__actions">
        <Button
          type="button"
          variant="secondary"
          onClick={onCancel}
          disabled={isSaving}
        >
          Cancel
        </Button>
        <Button type="submit" variant="primary" disabled={isSaving}>
          {isSaving ? 'Saving...' : product ? 'Save Changes' : 'Create Product'}
        </Button>
      </div>
    </form>
  );
};

ProductForm.propTypes = {
  product: PropTypes.object,
  choices: PropTypes.shape({
    grain_varieties: PropTypes.array,
    unit_choices: PropTypes.array,
    growers: PropTypes.array,
    category_choices: PropTypes.array,
  }),
  onSave: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
  isSaving: PropTypes.bool,
};

export default ProductForm;
